import React, { useCallback } from "react";
import { CruiseTemplateSuiteFragment } from "types/graphql-types";
import { cruiseIsWaitlist } from "../../../utils/cruise";
import {
  priceForSuite,
  suiteCardFeatures,
  isSuitePriceWaitlist
} from "src/utils/suites";
import { getSuiteSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { UnitSystem } from "src/utils/localization/geolocation/geolocation";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import {
  ActivePromoForCruise,
  activePromosToPromoBadges
} from "src/utils/promos";
import { isPresent } from "src/utils/checks";
import HorizontalCruiseSuiteCard from "src/page-components/cruise/suites/HorizontalCruiseSuiteCard";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import { SuitePriceV2 } from "@silversea-ssc/dotcom-api-types/CruisePriceAndOffersV2";
import { CruiseSuiteOpenModalArgs } from "./SectionSuites";
import { useLingui } from "@lingui/react/macro";
import { suitePricestoPrices } from "components/Prices/utils";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { fakeSuiteUpgradePromo } from "./hackPromoToBeDeleted";

type Props = {
  suite: CruiseTemplateSuiteFragment;
  cruisePrice: CruisePriceAndOffersV2Response | undefined;
  cruiseCode: string;
  shipId: string;
  ecommerceStatus: ECommerceStatus;
  unitSystem?: UnitSystem;
  setOpenModal: (suite: CruiseSuiteOpenModalArgs) => void;
  totalDecks: number | undefined;
  activePromos: ActivePromoForCruise[];
  raqPagePath: string | undefined;
};

export const MosaicSuiteItem: React.FC<React.PropsWithChildren<Props>> = ({
  suite,
  cruisePrice,
  cruiseCode,
  shipId,
  ecommerceStatus,
  unitSystem,
  totalDecks,
  setOpenModal,
  activePromos,
  raqPagePath
}) => {
  const { t } = useLingui();
  const geolocation = useGeolocation();
  const suiteCodes = (suite.configurations?.map((x) => x?.code) || []).filter(
    isPresent
  );

  const suitePrice = isPresent(cruisePrice?.suitePrices)
    ? suitePricestoPrices(
        t,
        suiteCodes,
        cruisePrice?.suitePrices,
        geolocation.defaultCurrency,
        true,
        "without-from"
      )
    : undefined;

  const actualSuiteCategory = suitePrice?.suiteCategory;
  const originalSuitePrice = suitePrice?.originalSuitePrice;

  const isCruiseWaitlist = cruiseIsWaitlist(cruisePrice);
  const isWaitlist = suitePrice
    ? isSuitePriceWaitlist(suitePrice.prices)
    : true;
  const fakePromo = isWaitlist
    ? undefined
    : fakeSuiteUpgradePromo(suite, activePromos);

  const promoBadges = originalSuitePrice
    ? activePromoBadgesForSuite(originalSuitePrice, activePromos, fakePromo)
    : [];

  const raqUrl = getSuiteSpecificRAQ(
    raqPagePath,
    cruiseCode,
    shipId,
    actualSuiteCategory
  );

  const onDetailsOnClick = useCallback(() => {
    if (suitePrice) {
      setOpenModal({
        type: "suite",
        suiteCode: suitePrice.suiteCategory,
        prices: suitePrice.prices,
        raqCTAUrl: raqUrl,
        ecommerceStatus: ecommerceStatus
      });
    }
  }, [raqUrl, setOpenModal, suitePrice, ecommerceStatus]);

  if (!suitePrice) {
    return null;
  }

  const features = suiteCardFeatures(
    t,
    suite,
    suitePrice.suiteCategory,
    totalDecks,
    unitSystem
  );

  return (
    <HorizontalCruiseSuiteCard
      title={suite?.card?.title?.localized}
      image={suite?.card?.image}
      prices={suitePrice.prices}
      raqCTAUrl={raqUrl}
      ecommerceStatus={ecommerceStatus}
      trackingName={`suite_${suitePrice.suiteCategory}`}
      detailsOnClick={onDetailsOnClick}
      isCruiseWaitlist={isCruiseWaitlist}
      features={features}
      activePromos={promoBadges}
    />
  );
};

const promoConfigurationCodesFromDiscountedPrices = (
  prices: SuitePriceV2
): string[] => [
  ...new Set(
    prices.prices
      .flatMap((p) =>
        p.price.type == "discounted" ? p.price.promoConfigurationCodes : []
      )
      .filter(isPresent)
  )
];

const activePromoBadgesForSuite = (
  suitePrice: SuitePriceV2 | undefined,
  activePromos: ActivePromoForCruise[],
  fakePromo: ActivePromoForCruise | undefined
) => {
  const promoConfigurationCodes = suitePrice
    ? promoConfigurationCodesFromDiscountedPrices(suitePrice)
    : [];

  const promos = activePromos.filter((p) =>
    promoConfigurationCodes.includes(p.code)
  );

  const promosWithHack = [...promos, fakePromo].filter(isPresent);
  return activePromosToPromoBadges(promosWithHack);
};
